/**
 * Framework FieldDefinition catalog hook for the rules_engine endpoint.
 * Sibling to `useConditionFieldRegistry` (legacy assignments endpoint) — same
 * shape, different URL + params. Both delegate to [[useConditionFieldsFetcher]]
 * for the cancellation token, value-digest debounce, and `enabled`-gated state
 * reset.
 *
 * Fetches from
 * `GET /sysconfig/api/v1/organizations/{org_id}/rules_engine/condition_fields`
 * with `rule_set_type` + `event_key` + LOB scope + sub-org scope. The BE filters
 * the catalog by event scope (claim-scope events drop `exposure.*` /
 * `involved_party.*`); `event_key` is always forwarded so that filter is applied.
 *
 * The LOB scope is a list plus an all-LOBs flag: the BE intersects configured
 * fields, incident types and select options across every LOB in scope, which is
 * the same scope save-time validation builds from the rule's own `lobs`.
 * Consumers whose rules carry a single LOB pass a one-element list; an
 * all-LOBs rule sets `isAllLobs` and the BE expands it to the organization's
 * supported LOBs, exactly as `_scope_for_save` does for the saved rule.
 *
 * `enabled` is the caller-owned gate: skip the fetch entirely (and reset state)
 * when prerequisites aren't met. The hook is consumer-agnostic — each
 * per-consumer Formik wrapper composes its own prereq predicate (e.g. comms
 * gates on event+LOB+sub-org; exposures locks event and gates on LOB+sub-org).
 */

import type { FieldDefinitionDto } from './types';
import { useConditionFieldsFetcher } from './useConditionFieldsFetcher';

export interface UseRuleSetConditionFieldRegistryParams {
  organizationId: number;
  /** Caller's prerequisite predicate. When false the hook resets state and
   *  does not fire a request. */
  enabled: boolean;
  /** Consumer key registered via `ServiceRegistrationInfo.get_rule_set_configs`
   *  (e.g. `'communication_rule'`). Required by the BE catalog endpoint. */
  ruleSetType: string;
  /** Event key for scope-aware filtering. The BE drops `exposure.*` /
   *  `involved_party.*` paths from the catalog when the event's scope is
   *  `'claim'`. Omit for consumers whose rules don't dispatch via an event —
   *  the catalog returns the unfiltered shape. */
  eventKey?: string;
  /** Every LOB the rule is scoped to. The catalog is the intersection across
   *  all of them, so passing a subset offers fields the saved rule would be
   *  rejected for — or, worse, ones it can never match on. */
  lobs: string[];
  /** True when the rule is scoped to every LOB, present and future (`is_all_lobs`). The BE
   *  expands it to the organization's supported LOBs, which is what `_scope_for_save` does
   *  for the saved rule — so a snapshot of today's LOBs sent as `lobs` would mean something
   *  different and would disagree with the save path as soon as the org adds one. */
  isAllLobs?: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
}

export interface UseRuleSetConditionFieldRegistryResult {
  fields: FieldDefinitionDto[];
  isLoading: boolean;
  isError: boolean;
}

export const useRuleSetConditionFieldRegistry = ({
  organizationId,
  enabled,
  ruleSetType,
  eventKey,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
}: UseRuleSetConditionFieldRegistryParams): UseRuleSetConditionFieldRegistryResult =>
  useConditionFieldsFetcher({
    url: `/sysconfig/api/v1/organizations/${organizationId}/rules_engine/condition_fields`,
    enabled,
    queryParams: {
      rule_set_type: ruleSetType,
      // Consumers that don't dispatch via an event (e.g. exposure_rule)
      // omit `eventKey`. The BE `_excluded_contexts_for_event` treats an
      // unknown or empty event_key as "no filter" — same shape as omitting
      // it. Coerced to empty string so it survives the strict
      // `QueryParamValue` typing.
      event_key: eventKey ?? '',
      lobs,
      is_all_lobs: isAllLobs ?? false,
      is_all_sub_orgs: isAllSubOrgs,
      sub_org_ids: subOrgIds ?? [],
    },
  });
